import type {
    NdcSwitchConfig,
    NdcSwitchConfigV2,
    NdcSwitchTarget,
    NdcSwitchTargetV2,
} from '../../../types/drug-config';
import { PHARMACY_NPIS } from '../../pharmacies';
import type { MedicationProduct, OnHoldConfig } from '../../shared';

const penToKp: Omit<NdcSwitchTarget, 'ndc'> = {
    heading: 'Zepbound® Multi-Dose KwikPen®',
    description: [
        'To pay out-of-pocket for your prescription, it must be changed from a Single-Dose pen to Multi-Dose KwikPen® or vial.',
        '<strong>Insurance cannot be used for KwikPen® or vials, so ensure you are comfortable with self pay before making this switch.</strong>',
        '<strong>Once you request the switch, your prescription(s) will be placed on hold while we contact your provider to request an updated prescription.</strong>',
        '<strong>Please be aware that this change will apply to all Zepbound® prescriptions included in your treatment plan.</strong>',
    ],
    shortDescription:
        "Zepbound® Multi-Dose KwikPen® provides a full month's supply in one pen. Attach a new needle before each dose and you're ready to inject.",
    quantity: 1,
    disclaimer:
        '*Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® for an approved use consistent with FDA-approved product labeling.',
    confirmation: [
        "We're reaching out to your health provider to request a change so you can pay out-of-pocket. This request applies to all Zepbound® prescriptions in your treatment plan and may take some time.",
        "<strong>We recommend calling your doctor's office to confirm the change, as this can help speed up the process.</strong>",
    ],
};

const penToVial: Omit<NdcSwitchTarget, 'ndc'> = {
    heading: 'Zepbound® Single-Dose Vial',
    imageUrl: '/static/img/zepbound-sdv.png',
    description: [
        'To pay out-of-pocket for your prescription, it must be changed from a Single-Dose pen to Multi-Dose KwikPen® or vial.',
        '<strong>Insurance cannot be used for KwikPen® or vials, so ensure you are comfortable with self pay before making this switch.</strong>',
        '<strong>Once you request the switch, your prescription(s) will be placed on hold while we contact your provider to request an updated prescription.</strong>',
        '<strong>Please be aware that this change will apply to all Zepbound® prescriptions included in your treatment plan.</strong>',
    ],
    shortDescription:
        'Zepbound® Vial is intended for a single, pre-measured dose, helping users clearly identify and use one dose at a time.',
    quantity: 1,
    disclaimer:
        '*Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® vial for an approved use consistent with FDA-approved product labeling.',
    confirmation: [
        "We're reaching out to your health provider to request a change so you can pay out-of-pocket. This request applies to all Zepbound® prescriptions in your treatment plan and may take some time.",
        "<strong>To help move things along, please call your provider's office to confirm they have received the request and submitted any updated prescriptions to the pharmacy.</strong>",
    ],
};

const switchFromPen: Omit<NdcSwitchConfig, 'targets'> = {
    heading: 'Zepbound® self pay options',
    description:
        'To pay out-of-pocket for your prescription, it must be changed from a pen to any of the following options:',
    link: {
        heading: "Don't have insurance?",
        text: 'Looking for a self pay option?',
        position: 'description',
        icon: {
            url: '/static/img/vial-icon.svg',
        },
    },
};

// @TODO: this needs to be split into `OnHoldConfig` and `HoldScreenConfig` types (later)
const onHoldConfig: OnHoldConfig = {
    allowHold: false,
    pathways: [],
    screens: [],
    buttonText: '',
    modal: {
        title: '',
        body: [],
        buttonLabels: {
            close: '',
            confirm: '',
        },
    },
    holdScreen: {
        alert: {
            color: 'warning',
            body: [
                {
                    key: 'on-hold',
                    text: '<body1>Your current Zepbound® Single-Dose Pen prescription is on hold</body1>',
                },
            ],
        },
        body: {
            default: [
                {
                    key: 'default-1',
                    text: '<body1>You recently placed your Zepbound prescription(s) on hold so you could speak with your provider. All Zepbound prescriptions of this form in your treatment plan have been temporarily locked while the hold is in place.</body1>',
                },
                {
                    key: 'default-2',
                    text: "<body1>If you're ready to move forward, you can unlock the prescription(s) by confirming below. Once unlocked, the hold will be removed and the prescription(s) will be processed as-is.</body1>",
                },
            ],
            withChangeReq: {
                'self-pay': [
                    {
                        key: 'with-change-1',
                        text: "<body1>You've sent a fax to your healthcare provider to request a change from the {{currentDevice}} to <b>{{targetDevice}}</b> so you can use self-pay.</body1>",
                    },
                    {
                        key: 'with-change-2',
                        text: '<body1>This typically takes 1–3 business days. If you haven’t heard anything by then, you can reach out to your provider’s office.</body1>',
                    },
                ],
                'medicare-bridge': [
                    {
                        key: 'with-change-1',
                        text: "<body1>You've sent a fax to your healthcare provider to request a change from the {{currentDevice}} to <b>{{targetDevice}}</b> so you can proceed with the Medicare GLP‑1 Bridge program.</body1>",
                    },
                    {
                        key: 'with-change-2',
                        text: '<body1>This typically takes 1–3 business days. If you haven’t heard anything by then, you can reach out to your provider’s office.</body1>',
                    },
                ],
            },
        },
        title: {
            withChangeReq: 'Request submitted for:',
        },
        buttonLabels: {
            home: 'Return to home',
            unhold: 'Continue with current prescription',
        },
        confirmModal: {
            title: 'Are you sure?',
            body: {
                'self-pay': [
                    'You recently requested to change your Zepbound® prescription(s) to the {{targetDevice}}. Your request has been sent, and all Zepbound prescriptions of this form in your treatment plan are on hold while we wait for a response.',
                    "If you've changed your mind and want to proceed with the {{currentDevice}}, you can remove the hold by confirming below and the prescription will be processed as-is. If your provider sends a new prescription anyway, we'll notify you.",
                ],
                'medicare-bridge': [
                    'You recently requested to change your Zepbound® prescription(s) to the {{targetDevice}}. We’ve sent the request to your provider, and all Zepbound prescriptions of this form in your treatment plan are on hold while we wait for a response.',
                    "If you've changed your mind and want to proceed with the {{currentDevice}}, you can remove the hold by confirming below and the prescription will be processed as-is. We’ll stop sending reminders to your provider about this request. If they send a new prescription anyway, we’ll notify you.",
                    'As a reminder, your current prescription ({{currentDevice}}) is not eligible for the Medicare GLP‑1 Bridge program. To continue with the Medicare GLP‑1 Bridge program, you’ll need a prescription for the {{targetDevice}}.',
                ],
            },
            checkboxLabel:
                "I confirm that I've decided to keep the relevant prescription(s). I understand that I no longer expect a prescription change from my provider, and it is my responsibility to notify them that I am proceeding with the original prescription(s).",
            buttonLabels: {
                confirm: 'Confirm and continue',
            },
        },
        toast: {
            default: 'A request has been sent to your doctor.',
        },
    },
};

const disclosures: MedicationProduct['disclosures'] = {
    disclaimers: [
        {
            headlinePriceTypes: ['CopayAssistance'],
            slots: ['path-select-card', 'price-card'],
            content: [
                {
                    key: 'terms-and-conditions',
                    text: '<caption>*For eligible and commercially insured patients with coverage for Zepbound, governmental beneficiaries excluded, <termsModal>terms and conditions</termsModal> apply. Check your insurance coverage by calling the number on the back of your benefit card.</caption>',
                    richComponents: {
                        termsModal: {
                            type: 'modal',
                            props: {
                                size: 'sm',
                                title: 'Zepbound® Single-Dose Pen Terms and Conditions',
                                body: [
                                    {
                                        key: 'line-1',
                                        text: '<body1>If eligible and commercially insured with coverage for Zepbound®. Governmental beneficiaries excluded, <a href="https://www.lilly.com/lillydirect/medicines/zepbound/zepbound-tirzepatide-full-terms-conditions#commercial">terms and conditions</a> apply. Please see Indication and Safety Summary with Warnings at <a href="https://www.zepbound.lilly.com/risk">www.zepbound.lilly.com/risk</a>.</body1>',
                                    },
                                ],
                            },
                        },
                    },
                },
            ],
        },
    ],
};

function makeSwitchConfig(
    kwikpenNdc: string,
    kwikpenImageUrl: string,
    vialNdc: string
): NdcSwitchConfig {
    return {
        ...switchFromPen,
        targets: [
            {
                ...penToKp,
                ndc: kwikpenNdc,
                imageUrl: kwikpenImageUrl,
                imageOrientation: 'landscape',
            },
            { ...penToVial, ndc: vialNdc },
        ],
    };
}

function makeSelfPaySwitchConfigV2(
    kwikpenNdc: string,
    vialNdc: string
): NdcSwitchConfigV2 {
    const vialTarget: NdcSwitchTargetV2 = {
        ndc: vialNdc,
        description: 'Zepbound® Single-Dose Vial',
        iconUrl: '/static/img/vial-small.png',
        learnMoreModal: {
            title: 'Zepbound® Single-Dose Vial',
            description: [
                'The single-dose vial holds one dose of Zepbound® medicine (each monthly prescription contains four once-weekly vials). Each injection uses a new needle and syringe, which you can purchase at checkout. Vial is available exclusively through LillyDirect self-pay.',
            ],
        },
    };
    const kwikpenTarget: NdcSwitchTargetV2 = {
        ndc: kwikpenNdc,
        description: 'Zepbound® Multi-Dose KwikPen®',
        iconUrl: '/static/img/kwikpen-small.png',
        learnMoreModal: {
            title: 'Zepbound® Multi-Dose KwikPen® for Single Patient Use',
            description: [
                'Zepbound® KwikPen® is a multi-dose pen that holds four doses of Zepbound® medicine, one for each week of the month. Each injection uses a new pen needle, and you can purchase pen needles at checkout.',
            ],
        },
    };

    return {
        'self-pay': [
            {
                source: {
                    description: 'Zepbound® Single-Dose Pen',
                    iconUrl: '/static/img/autoinjector-small.png',
                    keepCurrent: {
                        returnPathway: 'previous',
                        returnSubPathway: 'previous',
                        returnScreen: 'path-select',
                    },
                },
                content: {
                    pageTitle: 'Request a new prescription',
                    pageDescription: [
                        "To use self-pay, you'll need to switch from a Zepbound® Single-Dose Pen to either a <b>Zepbound® Multi-Dose KwikPen®</b> or <b>Zepbound® Single-Dose Vial</b>.",
                    ],
                    details: {
                        whatsNext: [
                            'Click "Submit request" to send a fax to your healthcare provider requesting a prescription for the form of your choice. The request comes from you — we just handle the sending and receiving so you don\'t have to.',
                            'Once you request the new prescription(s), your current prescription will be placed on hold.',
                        ],
                        whatYouGet: {
                            bullets: [
                                {
                                    key: 'same-med',
                                    content:
                                        'Same medication, different device.',
                                    icon: 'check',
                                    iconColor: 'success',
                                },
                                {
                                    key: 'local-delivery',
                                    content:
                                        'Available for local pick-up or home-delivery.',
                                    icon: 'check',
                                    iconColor: 'success',
                                },
                                {
                                    key: 'ready-fast',
                                    content:
                                        'Ready for checkout as soon as your prescription is verified.',
                                    icon: 'check',
                                    iconColor: 'success',
                                },
                            ],
                        },
                        whySwitch: [
                            "The Zepbound® Single-Dose Pen is only available through insurance. To use self-pay, you'll need a prescription for either the Zepbound® Multi-Dose KwikPen® or the Zepbound® Single-Dose Vial.",
                        ],
                    },
                },
                targets: [kwikpenTarget, vialTarget],
            },
        ],
        government: [
            {
                source: {
                    description: 'Zepbound® Single-Dose Pen',
                    iconUrl: '/static/img/autoinjector-small.png',
                    keepCurrent: {
                        returnPathway: 'government',
                        returnSubPathway: 'medicare-bridge',
                        returnScreen: 'path-select',
                        modal: {
                            title: 'Are you sure?',
                            description: [
                                "As a reminder, your current prescription (Zepbound® Single-Dose Pen) is not available under the Medicare GLP\u20111 Bridge. To continue with the Medicare GLP\u20111 Bridge program, you'll need a prescription for the Zepbound® Multi-Dose KwikPen®.",
                            ],
                        },
                    },
                },
                content: {
                    pageTitle: 'Request a new prescription',
                    pageDescription: [
                        "To use Medicare GLP\u20111 Bridge, you'll need to switch from a Zepbound® Single-Dose Pen to the <b>Zepbound® Multi-Dose KwikPen®</b>.",
                    ],
                    details: {
                        whatsNext: [
                            'Click "Submit request" to send a fax to your healthcare provider requesting a prescription for the Zepbound® Multi-Dose KwikPen®. The request comes from you — we just handle the sending and receiving so you don\'t have to.',
                            'Once you request the new prescription(s), your current prescription will be placed on hold.',
                        ],
                        whatYouGet: {
                            bullets: [
                                {
                                    key: 'same-med',
                                    content:
                                        'Same medication, different device.',
                                    icon: 'check',
                                    iconColor: 'success',
                                },
                                {
                                    key: 'maybe-bridge',
                                    content:
                                        'May be covered by the Medicare GLP\u20111 Bridge program.',
                                    icon: 'check',
                                    iconColor: 'success',
                                },
                                {
                                    key: 'maybe-pa',
                                    content:
                                        '<b>Prior authorization (PA)</b><br />Medicare GLP\u20111 Bridge requires insurance approval with support from your provider, which may take time.',
                                    icon: 'warning',
                                    iconColor: 'warning',
                                },
                            ],
                        },
                        whySwitch: [
                            "To continue with the Medicare GLP\u20111 Bridge program, you'll need a prescription for the Zepbound® Multi-Dose KwikPen®.",
                        ],
                    },
                },
                subPathways: ['medicare-bridge'],
                targets: [kwikpenTarget],
            },
        ],
    };
}

export const zepboundAutoInjectors: MedicationProduct[] = [
    {
        description: 'Zepbound® 2.5 mg/0.5 mL Single-Dose Pen',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 2.5,
        ndcs: [
            { ndc: '00002250601', packageQuantity: 1 },
            { ndc: '00002250680', packageQuantity: 1 },
            { ndc: '00002250661', packageQuantity: 1 },
        ],
        pricing: { insurance: 25 },
        headlinePrices: [
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
            },
            { type: 'NonCoveredBenefit', value: 499 },
            {
                type: 'MedicarePartD',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'Retail',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        switchConfigV2: makeSelfPaySwitchConfigV2('00002356611', '00002015201'),
        switchConfig: makeSwitchConfig(
            '00002356611',
            '/static/img/zepbound-kwikpen-2.5mg.png',
            '00002015201'
        ),
        disclosures,
        onHoldConfig,
    },
    {
        description: 'Zepbound® 5 mg/0.5 mL Single-Dose Pen',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 5,
        ndcs: [
            { ndc: '00002249501', packageQuantity: 1 },
            { ndc: '00002249580', packageQuantity: 1 },
        ],
        pricing: { insurance: 25 },
        headlinePrices: [
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
            },
            { type: 'NonCoveredBenefit', value: 499 },
            {
                type: 'MedicarePartD',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'Retail',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        switchConfigV2: makeSelfPaySwitchConfigV2('00002355511', '00002024301'),
        switchConfig: makeSwitchConfig(
            '00002355511',
            '/static/img/zepbound-kwikpen-5mg.png',
            '00002024301'
        ),
        disclosures,
        onHoldConfig,
    },
    {
        description: 'Zepbound® 7.5 mg/0.5 mL Single-Dose Pen',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 7.5,
        ndcs: [
            { ndc: '00002248401', packageQuantity: 1 },
            { ndc: '00002248480', packageQuantity: 1 },
        ],
        pricing: { insurance: 25 },
        headlinePrices: [
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
            },
            { type: 'NonCoveredBenefit', value: 499 },
            {
                type: 'MedicarePartD',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'Retail',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        switchConfigV2: makeSelfPaySwitchConfigV2('00002354411', '00002121401'),
        switchConfig: makeSwitchConfig(
            '00002354411',
            '/static/img/zepbound-kwikpen-7.5mg.png',
            '00002121401'
        ),
        disclosures,
        onHoldConfig,
    },
    {
        description: 'Zepbound® 10 mg/0.5 mL Single-Dose Pen',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 10,
        ndcs: [
            { ndc: '00002247101', packageQuantity: 1 },
            { ndc: '00002247180', packageQuantity: 1 },
        ],
        pricing: { insurance: 25 },
        headlinePrices: [
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
            },
            { type: 'NonCoveredBenefit', value: 499 },
            {
                type: 'MedicarePartD',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'Retail',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        switchConfigV2: makeSelfPaySwitchConfigV2('00002353311', '00002134001'),
        switchConfig: makeSwitchConfig(
            '00002353311',
            '/static/img/zepbound-kwikpen-10mg.png',
            '00002134001'
        ),
        disclosures,
        onHoldConfig,
    },
    {
        description: 'Zepbound® 12.5 mg/0.5 mL Single-Dose Pen',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 12.5,
        ndcs: [
            { ndc: '00002246001', packageQuantity: 1 },
            { ndc: '00002246080', packageQuantity: 1 },
        ],
        pricing: { insurance: 25 },
        headlinePrices: [
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
            },
            { type: 'NonCoveredBenefit', value: 499 },
            {
                type: 'MedicarePartD',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'Retail',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        switchConfigV2: makeSelfPaySwitchConfigV2('00002352211', '00002142304'),
        switchConfig: makeSwitchConfig(
            '00002352211',
            '/static/img/zepbound-kwikpen-12.5mg.png',
            '00002142304'
        ),
        disclosures,
        onHoldConfig,
    },
    {
        description: 'Zepbound® 15 mg/0.5 mL Single-Dose Pen',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 15,
        ndcs: [
            { ndc: '00002245701', packageQuantity: 1 },
            { ndc: '00002245780', packageQuantity: 1 },
        ],
        pricing: { insurance: 25 },
        headlinePrices: [
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
            },
            { type: 'NonCoveredBenefit', value: 499 },
            {
                type: 'MedicarePartD',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'Retail',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        switchConfigV2: makeSelfPaySwitchConfigV2('00002351111', '00002200204'),
        switchConfig: makeSwitchConfig(
            '00002351111',
            '/static/img/zepbound-kwikpen-15mg.png',
            '00002200204'
        ),
        disclosures,
        onHoldConfig,
    },
];
